"use client";

/**
 * Sélecteur de langue FR⇄EN (global header + footer).
 *
 * Manuel par choix SEO : aucune redirection auto selon la langue du navigateur
 * (déconseillé par Google). On s'appuie sur hreflang + ce switcher. Chaque
 * bouton pointe vers l'équivalent EXACT de la page courante dans l'autre langue
 * (via localeAlternatePath), avec repli sur l'accueil si pas d'équivalent.
 *
 * `prefetch={false}` : on n'a pas besoin de précharger la version traduite de
 * chaque page visitée.
 */
import Link from "next/link";
import { usePathname } from "next/navigation";
import { LOCALES, type Locale, localeFromPath } from "@/config/i18n";
import { localeAlternatePath, localeHasPage } from "@/config/localeRoutes";
import { setPreferredLocale } from "@/components/i18n/preferredLocale";

const LABEL: Record<Locale, string> = { fr: "FR", en: "EN", ar: "ع" };
const FULL_LABEL: Record<Locale, string> = {
  fr: "Français",
  en: "English",
  ar: "العربية",
};

type Props = {
  /** "header" = compact sombre ; "footer" = sur fond noir du footer. */
  variant?: "header" | "footer";
  className?: string;
};

export function LanguageSwitcher({ variant = "header", className }: Props) {
  const pathname = usePathname() ?? "/";
  const current = localeFromPath(pathname);

  return (
    <div
      className={`inline-flex items-center gap-0.5 rounded-full border border-white/20 bg-black/30 p-0.5 ${className ?? ""}`}
      role="group"
      aria-label={current === "en" ? "Language" : "Langue"}
    >
      {LOCALES.filter(
        // Gating : on n'affiche une locale que si elle a une vraie page pour ce
        // chemin (ou si c'est la locale courante). L'arabe en cours de
        // déploiement n'apparaît donc que là où il est traduit → jamais de 404.
        (locale) => locale === current || localeHasPage(pathname, locale),
      ).map((locale) => {
        const isActive = locale === current;
        const href = localeAlternatePath(pathname, locale);
        const base =
          "rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wide transition-colors";
        const activeCls =
          variant === "footer"
            ? "bg-white text-black"
            : "bg-primary text-black";
        const idleCls = "text-white/70 hover:text-white";

        if (isActive) {
          return (
            <span
              key={locale}
              aria-current="true"
              className={`${base} ${activeCls}`}
            >
              {LABEL[locale]}
            </span>
          );
        }
        return (
          <Link
            key={locale}
            href={href}
            prefetch={false}
            hrefLang={locale}
            aria-label={FULL_LABEL[locale]}
            onClick={() => setPreferredLocale(locale)}
            className={`${base} ${idleCls}`}
          >
            {LABEL[locale]}
          </Link>
        );
      })}
    </div>
  );
}
